import React, { useState, useEffect } from "react"
import { List, Button, Space, message, Typography, Switch, Select, Tooltip, Input, Tag } from "antd"
import Modal from "../../components/modal/DraggableModal"   // talabnoma §8.2 (2026-09-06): siljitiladigan modal
import { ArrowUp, ArrowDown, RotateCcw, Menu as MenuIcon, Check, Eye, EyeOff } from "react-feather"
import menuItems, { MenuItem } from "../../data/menuItems"
import {
  MENU_GROUP_DEFS,
  MenuPrefs,
  MENU_PREFS_CACHE_KEY,
  defaultGroupOf,
  readCachedPrefs,
} from "../../data/menuGroups"
import { useTranslation } from "react-i18next"
import { request } from "../../utils/config"
import i18n from "../../services/i18n"
import { useSectionNames, sectionGroupKey, sectionKeyOfRoute } from "../../utils/sectionNames"   // tog'a #3481

const { Text } = Typography

interface Props {
  visible: boolean
  onClose: () => void
  onOrderSaved: () => void
}

// Egasi №4 (2026-08-31): har foydalanuvchi menyuni O'ZIGA moslaydi —
// tartib, yashirish, elementni boshqa guruhga ko'chirish, guruhlashni
// butunlay o'chirish. Standart (admin varianti) — MENU_GROUP_DEFS.
// Sozlama SERVERDA saqlanadi (MenuPrefs) — istalgan kompyuterda bir xil.
export default function MenuReorderModal({ visible, onClose, onOrderSaved }: Props) {
  const { t } = useTranslation()
  const secNames = useSectionNames()   // tog'a #3481: tizim ichidagi nomlar
  const [items, setItems] = useState<MenuItem[]>([])
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [groupOf, setGroupOf] = useState<Record<string, string | null>>({})
  const [flat, setFlat] = useState(false)
  const [saving, setSaving] = useState(false)
  // Egasi №2: foydalanuvchining O'Z guruhlari (icon = emoji, 2-to'lqin №1)
  const [customGroups, setCustomGroups] = useState<{ key: string; label: string; icon?: string }[]>([])
  const [newGroupName, setNewGroupName] = useState("")
  const [newGroupIcon, setNewGroupIcon] = useState("📁")
  // Egasi 2-to'lqin №2/№3: guruhlar tartibi va yashirilgan guruhlar
  const [groupOrder, setGroupOrder] = useState<string[]>([])
  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set())

  const keyOf = (m: MenuItem) => m.link || m.route

  // Yangi guruh uchun tanlanadigan emoji to'plami
  const GROUP_EMOJIS = ["📁", "⭐", "📦", "💰", "🛒", "👥", "📊", "📄", "🏭", "🚚", "⚙️", "🧰", "🏠", "🔔", "🗂️", "💼"]

  useEffect(() => {
    if (!visible) return
    const prefs: MenuPrefs = readCachedPrefs() || {}
    const order = prefs.order || []
    // tog'a 1797 (2026-09-13, Boshliq): «Главная панель» (route «/») tizimdan butunlay olib tashlandi —
    // menyu sozlash ro'yxatida ham chiqmasin (Sidebar allaqachon ko'rsatmaydi)
    const sorted = [...menuItems].filter((m) => m.route !== "/").sort((a, b) => {
      const ia = order.indexOf(keyOf(a))
      const ib = order.indexOf(keyOf(b))
      if (ia !== -1 && ib !== -1) return ia - ib
      if (ia !== -1) return -1
      if (ib !== -1) return 1
      return 0
    })
    setItems(sorted)
    setHidden(new Set(prefs.hidden || []))
    setGroupOf(prefs.groups || {})
    setFlat(!!prefs.flat)
    setCustomGroups(prefs.customGroups || [])
    setNewGroupName("")
    setNewGroupIcon("📁")
    // Guruh tartibi: saqlanganidan boshlaymiz, yo'q kalitlarni standart
    // ketma-ketlikda oxiriga qo'shamiz (yangi guruhlar ham ko'rinsin)
    const allKeys = [
      ...MENU_GROUP_DEFS.map((g) => g.key),
      ...(prefs.customGroups || []).map((g) => g.key),
    ]
    const savedOrder = (prefs.groupOrder || []).filter((k) => allKeys.includes(k))
    setGroupOrder([...savedOrder, ...allKeys.filter((k) => !savedOrder.includes(k))])
    setHiddenGroups(new Set(prefs.hiddenGroups || []))
  }, [visible])

  const move = (index: number, dir: -1 | 1) => {
    const j = index + dir
    if (j < 0 || j >= items.length) return
    const next = [...items]
    ;[next[index], next[j]] = [next[j], next[index]]
    setItems(next)
  }

  const buildPrefs = (): MenuPrefs => ({
    flat,
    hidden: Array.from(hidden),
    order: items.map(keyOf),
    groups: groupOf,
    customGroups,
    groupOrder,
    hiddenGroups: Array.from(hiddenGroups),
  })

  const addCustomGroup = () => {
    const label = newGroupName.trim()
    if (!label) return
    if (
      customGroups.some((g) => g.label === label) ||
      MENU_GROUP_DEFS.some((g) => g.label === label)
    ) {
      message.warning("Bunday nomli guruh allaqachon bor")
      return
    }
    const key = "u-" + Date.now()
    setCustomGroups([...customGroups, { key, label, icon: newGroupIcon }])
    setGroupOrder([...groupOrder, key])
    setNewGroupName("")
  }

  const removeCustomGroup = (key: string) => {
    setCustomGroups(customGroups.filter((g) => g.key !== key))
    setGroupOrder(groupOrder.filter((k) => k !== key))
    setHiddenGroups((prev) => {
      const n = new Set(prev)
      n.delete(key)
      return n
    })
    // shu guruhga biriktirilganlar standart guruhiga qaytadi
    const next: Record<string, string | null> = {}
    for (const [k, v] of Object.entries(groupOf)) if (v !== key) next[k] = v
    setGroupOf(next)
  }

  // Egasi 2-to'lqin №2: guruhni yuqoriga/pastga surish
  const moveGroup = (index: number, dir: -1 | 1) => {
    const j = index + dir
    if (j < 0 || j >= groupOrder.length) return
    const next = [...groupOrder]
    ;[next[index], next[j]] = [next[j], next[index]]
    setGroupOrder(next)
  }

  // Tartib bo'yicha guruh ta'riflari (standart + shaxsiy)
  const orderedGroupDefs = groupOrder
    .map((k) => {
      const d = MENU_GROUP_DEFS.find((g) => g.key === k)
      if (d) return { key: d.key, label: secNames[sectionGroupKey(d.key)] || (d.labelKey ? t(d.labelKey) : d.label), icon: undefined as string | undefined, custom: false }
      const c = customGroups.find((g) => g.key === k)
      if (c) return { key: c.key, label: c.label, icon: c.icon || "📁", custom: true }
      return null
    })
    .filter(Boolean) as { key: string; label: string; icon?: string; custom: boolean }[]

  const persist = async (prefs: MenuPrefs | null) => {
    setSaving(true)
    try {
      await request({
        url: "AccountAPI/MenuPrefs",
        method: "POST",
        data: { prefs: prefs ? JSON.stringify(prefs) : null },
      })
      if (prefs) localStorage.setItem(MENU_PREFS_CACHE_KEY, JSON.stringify(prefs))
      else localStorage.removeItem(MENU_PREFS_CACHE_KEY)
      // eski flat-order kaliti endi kerak emas
      localStorage.removeItem("menu_custom_order")
      onOrderSaved()
      return true
    } catch (e: any) {
      // server yetmasa ham lokal ishlasin (fallback)
      if (prefs) localStorage.setItem(MENU_PREFS_CACHE_KEY, JSON.stringify(prefs))
      else localStorage.removeItem(MENU_PREFS_CACHE_KEY)
      onOrderSaved()
      message.warning(i18n.t("menuSavedLocalOnly"))
      return true
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (await persist(buildPrefs())) {
      message.success(i18n.t("menuSavedMine"))
      onClose()
    }
  }

  const handleReset = async () => {
    setItems([...menuItems])
    setHidden(new Set())
    setGroupOf({})
    setFlat(false)
    setCustomGroups([])
    setGroupOrder(MENU_GROUP_DEFS.map((g) => g.key))
    setHiddenGroups(new Set())
    await persist(null)
    message.info(t("mroReset", "Standart (admin) ko'rinishga qaytarildi"))
  }

  const groupOptions = [
    { value: "__none__", label: t("mroNoGroup", "— Guruhsiz (yuqorida) —") },
    ...MENU_GROUP_DEFS.map((g) => ({ value: g.key, label: secNames[sectionGroupKey(g.key)] || (g.labelKey ? t(g.labelKey) : g.label) })),
    ...customGroups.map((g) => ({ value: g.key, label: (g.icon || "📁") + " " + g.label })),
  ]

  return (
    <Modal
      title={
        <Space size={8}>
          <MenuIcon size={18} color="#1890ff" />
          <span style={{ fontWeight: 700 }}>{t("mroTitle", "Menyu — shaxsiy sozlash")}</span>
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="reset" icon={<RotateCcw size={14} style={{ marginRight: 6 }} />} onClick={handleReset} loading={saving}>
          {t("mroResetBtn", "Standartga qaytarish")}
        </Button>,
        <Button key="cancel" onClick={onClose}>{t("cancel", "Bekor")}</Button>,
        <Button key="save" type="primary" loading={saving} icon={<Check size={14} style={{ marginRight: 6 }} />} onClick={handleSave}>
          {t("save", "Saqlash")}
        </Button>,
      ]}
      bodyStyle={{ maxHeight: "62vh", overflowY: "auto", paddingTop: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 8 }}>
        <Switch checked={flat} onChange={setFlat} />
        <div>
          <div style={{ fontWeight: 600 }}>{t("mroFlat", "Guruhlashni o'chirish (tekis ro'yxat)")}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("mroFlatHint", "Yoqilsa — barcha bo'limlar guruhsiz, bitta ro'yxat bo'lib chiqadi")}
          </Text>
        </div>
      </div>

      {/* Egasi №2: o'z guruhini yaratish — ikon (emoji) + nom kiritiladi, so'ng
          istalgan bo'limni pastdagi ro'yxatdan shu guruhga o'tkazasiz */}
      {!flat && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <Select
              size="small"
              style={{ width: 64 }}
              value={newGroupIcon}
              onChange={setNewGroupIcon}
              options={GROUP_EMOJIS.map((e) => ({ value: e, label: e }))}
              dropdownMatchSelectWidth={false}
            />
            <Input
              size="small"
              placeholder={t("mroNewGroup", "Yangi guruh nomi (masalan: Mening bo'limlarim)")}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onPressEnter={addCustomGroup}
            />
            <Button size="small" type="primary" onClick={addCustomGroup}>
              {t("mroCreateGroup", "➕ Guruh yaratish")}
            </Button>
          </div>
          {customGroups.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {customGroups.map((g) => (
                <Tag key={g.key} closable onClose={() => removeCustomGroup(g.key)}>
                  {g.icon || "📁"} {g.label}
                </Tag>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Egasi 2-to'lqin №2/№3: GURUHLAR — tartibini surish va butunlay yashirish.
          Yashirilgan guruh ichidagi bo'limlar ham menyudan chiqadi («Главная» qoladi) */}
      {!flat && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f8fafc", borderRadius: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{t("mroGroups", "Guruhlar — tartib va ko'rinish")}</div>
          {orderedGroupDefs.map((g, idx) => {
            const gHidden = hiddenGroups.has(g.key)
            return (
              <div
                key={g.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "3px 4px",
                  opacity: gHidden ? 0.45 : 1,
                }}
              >
                <span style={{ flex: 1, fontSize: 13 }}>
                  {g.icon ? g.icon + " " : ""}
                  {g.label}
                </span>
                <Tooltip title={gHidden ? t("mroGroupShow", "Guruhni ko'rsatish") : t("mroGroupHide", "Guruhni menyudan yashirish (ichidagilar bilan)")}>
                  <Button
                    size="small"
                    type="text"
                    icon={gHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                    onClick={() => {
                      const next = new Set(hiddenGroups)
                      if (gHidden) next.delete(g.key)
                      else next.add(g.key)
                      setHiddenGroups(next)
                    }}
                  />
                </Tooltip>
                <Button size="small" type="text" disabled={idx === 0}
                  icon={<ArrowUp size={14} />} onClick={() => moveGroup(idx, -1)} />
                <Button size="small" type="text" disabled={idx === orderedGroupDefs.length - 1}
                  icon={<ArrowDown size={14} />} onClick={() => moveGroup(idx, 1)} />
              </div>
            )
          })}
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t("mroHiddenHint", "Yashirilgan guruh bo'limlari ham menyudan chiqadi; «Главная» har doim qoladi.")}
          </Text>
        </div>
      )}

      <List
        size="small"
        dataSource={items}
        renderItem={(item, index) => {
          const k = keyOf(item)
          const isHidden = hidden.has(k)
          const curGroup = groupOf[k] === undefined ? defaultGroupOf(item.title) : groupOf[k]
          return (
            <List.Item
              style={{ opacity: isHidden ? 0.45 : 1, padding: "6px 8px" }}
              actions={[
                <Tooltip key="hide" title={isHidden ? t("mroShow", "Ko'rsatish") : t("mroHide", "Menyudan yashirish")}>
                  <Button
                    size="small"
                    type="text"
                    icon={isHidden ? <EyeOff size={15} /> : <Eye size={15} />}
                    onClick={() => {
                      const next = new Set(hidden)
                      if (isHidden) next.delete(k)
                      else next.add(k)
                      setHidden(next)
                    }}
                  />
                </Tooltip>,
                !flat ? (
                  <Select
                    key="grp"
                    size="small"
                    style={{ width: 150 }}
                    value={curGroup ?? "__none__"}
                    options={groupOptions}
                    onChange={(v) =>
                      setGroupOf({ ...groupOf, [k]: v === "__none__" ? null : v })
                    }
                  />
                ) : null,
                <Button key="up" size="small" type="text" disabled={index === 0}
                  icon={<ArrowUp size={15} />} onClick={() => move(index, -1)} />,
                <Button key="dn" size="small" type="text" disabled={index === items.length - 1}
                  icon={<ArrowDown size={15} />} onClick={() => move(index, 1)} />,
              ].filter(Boolean) as any}
            >
              <Space size={8}>
                <item.icon size={15} />
                <span style={{ fontWeight: 500 }}>{secNames[sectionKeyOfRoute((item as any).link || item.route)] || t(item.title)}</span>
              </Space>
            </List.Item>
          )
        }}
      />
      <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
        {t("mroFooter", "Sozlama faqat SIZGA tegishli — serverda saqlanadi, istalgan kompyuterda bir xil ishlaydi. Standart ko'rinishni admin belgilaydi; «Standartga qaytarish» bilan qaytasiz.")}
      </Text>
    </Modal>
  )
}
